import React, { useState, useEffect } from "react";
import {
  Box, Button, TextField, MenuItem, Typography, Grid,
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Paper, Stack, Divider, TableContainer, useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";

import { getClientes } from "../../../services/modules/Clientes";
import { insertarReparacion } from "../../../services/modules/IngresarReparacion";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoPerfect from "../../../assets/Logo.png";
import { BRANDING } from "../../../config/branding";

// Helpers
const hoyISO = () => {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10); // yyyy-MM-dd
};

// PDF
const generarPDF = (reparaciones, cliente, id_reparacion, fechaISO) => {
  const fechaFormateada = fechaISO ? fechaISO.split("-").reverse().join("/") : "";

  const doc = new jsPDF();
  const img = new Image();
  img.src = logoPerfect;

  img.onload = () => {
    // Logo
    doc.addImage(img, "jpeg", 10, 10, 60, 30);

    // Título
    doc.setFontSize(18);
    doc.text("COMPROBANTE DE INGRESO", 105, 25, { align: "center" });

    // Empresa
    doc.setFontSize(12);
    const companyX = 15;
    const companyY = 45;
    doc.text(BRANDING.pdf.name, companyX, companyY);
    BRANDING.pdf.addressLines.forEach((line, index) => {
      doc.text(line, companyX, companyY + 6 * (index + 1));
    });
    doc.text(
      `Teléfono: ${BRANDING.phoneText}`,
      companyX,
      companyY + 6 * (BRANDING.pdf.addressLines.length + 1)
    );

    // Cliente
    doc.text(`Cliente: ${cliente?.nombre || "-"}`, 140, 45);
    doc.text(`NIT: ${cliente?.nit || "CF"}`, 140, 51);
    doc.text(`Teléfono: ${cliente?.telefono || "-"}`, 140, 57);
    doc.text(`Fecha: ${fechaFormateada}`, 140, 63);
    doc.text(`Comprobante #: ${id_reparacion || "-"}`, 140, 69);

    // Tabla
    const rows = reparaciones.map((r) => [
      1,
      `${r.equipo || ""} ${r.marca || ""} ${r.modelo || ""}`.trim(),
      r.problema_reportado || "",
      "-",
      "-",
    ]);

    autoTable(doc, {
      startY: 70,
      head: [["Cant.", "Descripción", "Problema Reportado", "Precio Unitario", "Total"]],
      body: rows,
      styles: {
        halign: "center",
        fontSize: 10,
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
        cellPadding: 3,
        textColor: 20
      },
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255]
      },
      theme: "grid"
    });

    const yFinal = doc.lastAutoTable.finalY + 10;

    // Total equipos
    doc.setFontSize(14);
    doc.text(`Total de Equipos: ${reparaciones.length}`, 150, yFinal);

    // Mensaje
    doc.setFontSize(11);
    doc.text(
      "Después de realizar la reparación tiene 45 días para recoger su equipo,\ndespués no nos hacemos responsables.",
      15,
      yFinal + 15
    );

    // Firma
    doc.setFontSize(12);
    doc.text("Recibí Conforme", 105, yFinal + 40, { align: "center" });
    doc.text("F._______________________________________", 105, yFinal + 50, { align: "center" });

    doc.save(`comprobante_reparacion_${(cliente?.nombre || "cliente").replace(/\s+/g, "_").toLowerCase()}.pdf`);
  };
};

const IngresoReparaciones = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [clientes, setClientes] = useState([]);
  const [idCliente, setIdCliente] = useState("");
  const [fechaIngreso, setFechaIngreso] = useState(hoyISO()); // yyyy-MM-dd

  const [equipo, setEquipo] = useState("");
  const [marca, setMarca] = useState("");
  const [modelo, setModelo] = useState("");
  const [serie, setSerie] = useState("");
  const [problema, setProblema] = useState("");

  const [reparaciones, setReparaciones] = useState([]);

  useEffect(() => {
    const cargarClientes = async () => {
      try {
        const data = await getClientes();
        setClientes(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Error al cargar clientes:", e);
        setClientes([]);
      }
    };
    cargarClientes();
  }, []);

  const agregarReparacion = () => {
    if (!idCliente || !equipo.trim() || !problema.trim()) {
      alert("Complete los campos obligatorios: cliente, equipo y problema");
      return;
    }

    const nueva = {
      id_cliente: idCliente,
      equipo: equipo.trim(),
      marca: marca.trim(),
      modelo: modelo.trim(),
      serie: serie.trim(),
      problema_reportado: problema.trim(),
      fecha_ingreso: fechaIngreso, // yyyy-MM-dd
      estado: "Ingresado"
    };

    setReparaciones((prev) => [...prev, nueva]);

    // limpiar inputs del ítem
    setEquipo("");
    setMarca("");
    setModelo("");
    setSerie("");
    setProblema("");
  };

  const eliminarReparacion = (index) => {
    setReparaciones((prev) => prev.filter((_, i) => i !== index));
  };

  const guardarReparaciones = async () => {
    if (!reparaciones.length) {
      alert("Agrega al menos un equipo.");
      return;
    }

    try {
      const payload = reparaciones.map((r) => ({ ...r, id_cliente: idCliente }));
      const res = await insertarReparacion(payload);
      alert(res?.message || "Reparaciones guardadas correctamente");

      const cliente = clientes.find((c) => c.id_cliente === idCliente);
      try {
        generarPDF(payload, cliente, res?.id_reparacion || "-", fechaIngreso);
      } catch (pdfError) {
        console.error("Error al generar el PDF:", pdfError);
        alert("Las reparaciones se guardaron, pero hubo un error al generar el PDF.");
      }

      // reset
      setReparaciones([]);
      // si quieres resetear también el cliente, descomenta:
      // setIdCliente(""); setFechaIngreso(hoyISO());
    } catch (error) {
      console.error("Error al guardar reparaciones:", error);
      alert("Error al guardar reparaciones.");
    }
  };

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Typography variant="h5" gutterBottom>
        Ingreso de Equipos para Reparación
      </Typography>

      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Cliente"
              value={idCliente}
              onChange={(e) => setIdCliente(e.target.value)}
              fullWidth
            >
              {clientes.map((cli) => (
                <MenuItem key={cli.id_cliente} value={cli.id_cliente}>
                  {cli.nombre}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Fecha de Ingreso"
              type="date"
              value={fechaIngreso}
              onChange={(e) => setFechaIngreso(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField label="Equipo" value={equipo} onChange={(e) => setEquipo(e.target.value)} fullWidth />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField label="Marca" value={marca} onChange={(e) => setMarca(e.target.value)} fullWidth />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField label="Modelo" value={modelo} onChange={(e) => setModelo(e.target.value)} fullWidth />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField label="Serie" value={serie} onChange={(e) => setSerie(e.target.value)} fullWidth />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Problema Reportado"
              value={problema}
              onChange={(e) => setProblema(e.target.value)}
              fullWidth
              multiline
            />
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" onClick={agregarReparacion}>
              Agregar Reparación
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          Lista Temporal de Reparaciones
        </Typography>

        {/* Móvil: tarjetas; Desktop: tabla */}
        {isXs ? (
          <Box>
            {reparaciones.length === 0 && (
              <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 0 }}>
                <Typography align="center" color="text.secondary">Sin reparaciones</Typography>
              </Paper>
            )}

            {reparaciones.map((r, i) => (
              <Paper key={i} sx={{ p: 2, mb: 1.5, borderRadius: 2, boxShadow: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Box sx={{ fontWeight: 700 }}>
                      {`${r.equipo || ""} ${r.marca || ""} ${r.modelo || ""}`.trim()}
                    </Box>
                    <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                      Serie: {r.serie || "—"} • Fecha: {r.fecha_ingreso}
                    </Box>
                  </Box>
                  <IconButton onClick={() => eliminarReparacion(i)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Typography variant="caption" color="text.secondary">Problema reportado</Typography>
                <Typography variant="body2">{r.problema_reportado || "—"}</Typography>
              </Paper>
            ))}

            {reparaciones.length > 0 && (
              <Paper sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2">Total de equipos:</Typography>
                  <Typography variant="subtitle2" fontWeight={700}>{reparaciones.length}</Typography>
                </Stack>
              </Paper>
            )}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 560 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Equipo</TableCell>
                  <TableCell>Marca</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell>Serie</TableCell>
                  <TableCell>Problema</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Eliminar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reparaciones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Sin reparaciones
                    </TableCell>
                  </TableRow>
                ) : (
                  reparaciones.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.equipo}</TableCell>
                      <TableCell>{r.marca}</TableCell>
                      <TableCell>{r.modelo}</TableCell>
                      <TableCell>{r.serie}</TableCell>
                      <TableCell>{r.problema_reportado}</TableCell>
                      <TableCell>{r.fecha_ingreso}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => eliminarReparacion(i)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {reparaciones.length > 0 && (
        <Box mt={3}>
          <Button variant="contained" color="success" onClick={guardarReparaciones}>
            Guardar Reparaciones
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default IngresoReparaciones;
